import { wccModules } from 'enums/wccModules';
import { AppearBindingOptions } from 'extensions/knockout/bindings/appear';
import { inject, injectable } from 'inversify';
import { SubscribableOrNullableValue } from 'knockout';
import { EffectsContainer } from 'mixins/withEffects';
import './offsetViewer.html';
import './offsetViewer.scss';

export interface HeatmapOffsetViewerConfig {
    viewportWidth: SubscribableOrNullableValue<number>
    viewportHeight: SubscribableOrNullableValue<number>
    imageWidth: SubscribableOrNullableValue<number>
    imageHeight: SubscribableOrNullableValue<number>
    dx: SubscribableOrNullableValue<number>
    dy: SubscribableOrNullableValue<number>
    visible?: SubscribableOrNullableValue<boolean>
}

@injectable()
export default class HeatmapOffsetViewerVM {
    frameNode = ko.observable<HTMLElement>();

    constructor(
        @inject(wccModules.componentConfig) config: HeatmapOffsetViewerConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const viewportWidth = ko.flattenComputed(config.viewportWidth, 0);
        const viewportHeight = ko.flattenComputed(config.viewportHeight, 0);

        const imageWidth = ko.flattenComputed(config.imageWidth, 0);
        const imageHeight = ko.flattenComputed(config.imageHeight, 0);

        const ratio = ko.strictComputed((width, height) => width > 0 && height > 0 ? width / height : 1, [imageWidth, imageHeight]);

        const frameWidth = ko.strictComputed((imageWidth, viewportWidth) => imageWidth > 0 && viewportWidth > 0 && imageWidth > viewportWidth ? viewportWidth / imageWidth * 100 : 100, [imageWidth, viewportWidth]);
        const frameHeight = ko.strictComputed((imageHeight, viewportHeight) => imageHeight > 0 && viewportHeight > 0 && imageHeight > viewportHeight ? viewportHeight / imageHeight * 100 : 100, [imageHeight, viewportHeight]);

        const dx = ko.flattenComputed(config.dx, 0);
        const dy = ko.flattenComputed(config.dy, 0);

        const frameDx = ko.strictComputed((dx, viewportWidth) => viewportWidth > 0 ? dx / viewportWidth * 100 : 0, [dx, viewportWidth]);
        const frameDy = ko.strictComputed((dy, viewportHeight) => viewportHeight > 0 ? dy / viewportHeight * 100 : 0, [dy, viewportHeight]);

        const isVisible = ko.flattenComputed(config.visible, true);

        effects.register(frameNode => {
            if (frameNode != undefined) {
                ko.applyBindingsToNode(frameNode, {
                    'style': {
                        'width': frameWidth.toPrts(),
                        'height': frameHeight.toPrts(),
                        'transform': ko.pureComputed(() => `translateX(${-frameDx()}%) translateY(${-frameDy()}%)`)
                    }
                });
            }
        }, [this.frameNode]);

        ko.applyBindingsToNode(info.element, {
            'appear': <AppearBindingOptions>{ visible: isVisible },

            'css': {
                'wcc-heatmap__offset-viewer': true
            },

            'style': {
                'padding-top': ko.pureComputed(() => 1 / ratio() * 100).toPrts()
            }
        });
    }
}